import restClient from 'config/httpClient';
import { call, put, takeLatest } from 'redux-saga/effects';

import { getLocalStorageItem } from 'utils/localStorage';
import { createNotification } from 'modules/notifications/actions';
import {
  COOKIES_STORAGE_KEY,
  DEFAULT_FAILURE_MESSAGE,
  DEFAULT_SUCCESS_MESSAGE_BY_METHOD,
  END_REQUEST,
  FAILURE_REQUEST,
  NOTIFIABLE_METHODS,
  SUCCESS_REQUEST,
} from './constants';

/**
 * Check in the localstorage if cookies were already accepted
 * @returns {boolean}
 */
export const hasAnsweredCookies = () => Boolean(getLocalStorageItem(COOKIES_STORAGE_KEY));

/**
 * @param {import('types/generic.types').Action<unknown>} action
 * @returns {boolean}
 */
export const isAction = (action) => typeof action === 'object' && typeof action.type === 'string';

/**
 * @param {string} type
 * @returns {boolean}
 */
export const isRequestActionType = (type) => typeof type === 'string' && type.endsWith(END_REQUEST);

/**
 * @param {import('types/generic.types').Request<unknown, unknown>} request
 * @returns {boolean}
 */
export const hasRequestFormat = (request) => typeof request?.url === 'string' && typeof request.method == 'string';

/**
 * @param {import('types/generic.types').ActionRequest<unknown>} action
 * @returns {boolean}
 */
export const isActionRequest = (action) =>
  isAction(action) && isRequestActionType(action.type) && hasRequestFormat(action.payload.request);

/**
 * @param {string} type
 * @returns {string}
 * @throws {Error} The given type should be a request type - suffix should be REQUEST
 */
export const getBaseActionType = (type) => {
  if (!isRequestActionType(type)) {
    throw new Error(`Given type (${type}) does not match a request action type`);
  }
  return type.substring(0, type.length - END_REQUEST.length);
};

/**
 *
 * @param {import('types/generic.types').ActionRequest<unknown>} action
 * @returns {boolean}
 */
export const isAutoNotifiableRequest = (action) =>
  isActionRequest(action) && NOTIFIABLE_METHODS.includes(action.payload.request.method.toUpperCase());

/**
 * @param {unknown} data
 * @param {Object} _ - Action meta
 * @param {import('types/generic.types').ActionRequest<unknown>} sourceAction
 * @returns {import('types/generic.types').Action<unknown>}
 */
export const generateSuccessCallback = (data, _, sourceAction) => {
  const baseType = getBaseActionType(sourceAction.type);
  return {
    type: `${baseType}${SUCCESS_REQUEST}`,
    payload: data,
    meta: {
      ...(sourceAction.meta ?? {}),
      '@app-auto-formatted': true,
    },
  };
};

/**
 * @param {string} error
 * @param {Object} _ - Action meta
 * @param {import('types/generic.types').ActionRequest<unknown>} sourceAction
 * @returns {import('types/generic.types').ErrorAction}
 */
export const generateFailureCallback = (error, _, sourceAction) => {
  const baseType = getBaseActionType(sourceAction.type);
  return {
    type: `${baseType}${FAILURE_REQUEST}`,
    payload: error,
    error: true,
    meta: {
      ...(sourceAction.meta ?? {}),
      '@app-auto-formatted': true,
    },
  };
};

/**
 * @param {'success' | 'failure'} type
 * @param {import('types/generic.types').SimpleRequestConfig} config
 */
export const getCallbackAction = (type, config) => {
  if (type !== 'success' && type !== 'failure') {
    throw new Error('Invalid type');
  }
  return config?.callbacks?.[type] ?? (type === 'success' ? generateSuccessCallback : generateFailureCallback);
};

/**
 * @param {'success' | 'failure'} type
 * @param {import('types/generic.types').SimpleRequestConfig} config
 * @param {import('types/generic.types').Action<unknown>} successAction
 * @param {import('types/generic.types').ActionRequest<unknown>} baseAction
 * @returns
 */
export function getNotificationMsgDescriptor(type, config, successAction, baseAction) {
  if (type !== 'success' && type !== 'failure') {
    throw new Error('Invalid type');
  }

  const isDefined = Boolean(config?.notifications?.[type]);
  if (isDefined) {
    const isFn = typeof config.notifications[type] === 'function';
    return isFn ? config.notifications[type](successAction, baseAction) : config.notifications[type];
  }

  if (isAutoNotifiableRequest(baseAction)) {
    const method = baseAction.payload.request.method.toUpperCase();
    return type === 'success' ? DEFAULT_SUCCESS_MESSAGE_BY_METHOD[method] : DEFAULT_FAILURE_MESSAGE;
  }
  return undefined;
}

/**
 * @param  {[
 *  import('types/generic.types').SimpleRequestConfig,
 *  import('types/generic.types').ActionRequest<unknown>
 * ]} args
 */
export function* simpleRequest(...args) {
  let notification;
  let type = 'success';
  const [config] = args;
  const action = args.pop();

  if (!isActionRequest(action)) {
    throw new Error(`Given type (${action.type}) does not match a request action type`);
  }

  try {
    const { data } = yield call(restClient.request, action.payload.request);
    const successAction = getCallbackAction('success', config)?.(data, action.meta, action);
    notification = getNotificationMsgDescriptor('success', config, successAction, action);
    yield put(successAction);
  } catch (error) {
    type = 'error';
    const failureAction = getCallbackAction('failure', config)?.(error, action.meta, action);
    yield put(failureAction);
    notification = getNotificationMsgDescriptor('failure', config, failureAction, action);
  }

  if (!action.meta.silent && notification) {
    yield put(createNotification({ type, message: notification }));
  }
}

export const simpleRequestsLoader = (requestsConfig) => Object.entries(requestsConfig).map(([actionType, config]) => {
  const effectFn = config?.effect ?? takeLatest;
  return effectFn(actionType, simpleRequest, config);
});
